import _objectSpread from "@babel/runtime/helpers/esm/objectSpread2";
var _dec, _class, _VideoPlayer, _Loading, _IconPlaySolid;
/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
/** @jsx jsx */
import { Component } from 'react';
import PropTypes from 'prop-types';
import { withStyle, jsx } from '@instructure/emotion';
import { uid } from '@instructure/uid';
import { IconPlaySolid } from '@instructure/ui-icons';
import VideoPlayerControls from './VideoPlayerControls';
import Loading from '../Loading';
import { translate } from '../../../constants/translated/translations';
import generateStyle from './styles';
import generateComponentTheme from './theme';
import { PAUSED, PLAYING, ENDED } from './videoStates';
export const SEEK_INTERVAL_SECONDS = 5;
export const JUMP_INTERVAL_SECONDS = 30;
export const MEDIA_ELEMENT_PROPS = ['onLoadedMetadata', 'onProgress', 'onTimeUpdate', 'onSeeked', 'onEnded'];

/**
---
category: components
experimental: true
---
**/
let VideoPlayer = (_dec = withStyle(generateStyle, generateComponentTheme), _dec(_class = (_VideoPlayer = class VideoPlayer extends Component {
  constructor(props) {
    super(props);
    this.handleKeyPress = e => {
      const currentTime = this.state.currentTime;
      const keyHandlers = {
        ArrowLeft: () => {
          this.seek(currentTime - SEEK_INTERVAL_SECONDS);
        },
        ArrowRight: () => {
          this.seek(currentTime + SEEK_INTERVAL_SECONDS);
        },
        PageUp: () => {
          this.seek(currentTime + JUMP_INTERVAL_SECONDS);
        },
        PageDown: () => {
          this.seek(currentTime - JUMP_INTERVAL_SECONDS);
        },
        ' ': () => {
          this.togglePlay();
        }
      };
      if (e.key in keyHandlers) {
        e.preventDefault();
        this.showControls();
        keyHandlers[e.key]();
      }
    };
    this.showControls = (hideControlsTimeout = 2500) => {
      if (this.props.alwaysShowControls) {
        return;
      }
      if (this._hideControlsTimeoutId) {
        clearTimeout(this._hideControlsTimeoutId);
      }
      this.setState({
        showControls: true
      }, () => {
        this._hideControlsTimeoutId = setTimeout(() => {
          if (this.state.state === PLAYING) {
            this.setState({
              showControls: false
            });
          }
        }, hideControlsTimeout);
      });
    };
    this.play = () => {
      this.video.play();
    };
    this.pause = () => {
      this.video.pause();
    };
    this.togglePlay = () => {
      if (this.state.state === PLAYING) {
        this.pause();
      } else {
        this.play();
      }
    };
    this.seek = time => {
      const duration = this.state.duration;
      this.video.currentTime = Math.min(Math.max(0, time), duration);
    };
    this.getDerivedStateFromVideoProps = () => {
      if (!this.video) {
        return;
      }
      const buffered = this.video.buffered;
      let state = this.video.paused ? PAUSED : PLAYING;
      if (this.video.ended) {
        state = ENDED;
      }
      this.setState({
        state,
        currentTime: this.video.currentTime,
        duration: this.video.duration,
        buffered: buffered.length > 0 ? buffered.end(buffered.length - 1) : 0
      }, () => {
        if (this.state.state === ENDED) {
          this.seek(0);
          this.showControls();
        }
      });
    };
    this.setVideoRef = el => {
      if (this.video === null) {
        this.video = el;
      }
    };
    this.hideSpinner = () => {
      this.setState({
        loadingSrc: false
      });
    };
    this.video = null;
    this.state = {
      state: PAUSED,
      loadingSrc: true,
      showControls: true,
      videoId: uid('VideoPlayer')
    };
  }
  componentDidMount() {
    this.props.makeStyles();
  }
  componentDidUpdate() {
    this.props.makeStyles();
  }
  componentWillUnmount() {
    // remove the video ref and stop applying video props
    this.video = null;
  }
  render() {
    const _this$props = this.props,
      src = _this$props.src,
      controls = _this$props.controls,
      styles = _this$props.styles,
      onError = _this$props.onError,
      showPlayOverlay = _this$props.showPlayOverlay;
    const state = this.state.state;
    const actions = {
      play: this.play,
      pause: this.pause,
      seek: this.seek,
      showControls: this.showControls,
      togglePlay: this.togglePlay
    };
    const wrapperProps = {
      css: styles.container,
      onKeyDown: this.handleKeyPress,
      onFocus: () => this.showControls(),
      onMouseMove: () => this.showControls(),
      onClick: this.togglePlay,
      tabIndex: 0,
      role: 'presentation',
      'aria-label': translate('ARIA_VIDEO_LABEL')
    };
    const updateMediaStateProps = MEDIA_ELEMENT_PROPS.reduce((obj, name) => _objectSpread(_objectSpread({}, obj), {}, {
      [name]: this.getDerivedStateFromVideoProps
    }), {});

    /* eslint-disable jsx-a11y/media-has-caption, jsx-a11y/no-noninteractive-tabindex */
    return jsx("div", wrapperProps, this.state.loadingSrc && (_Loading || (_Loading = jsx(Loading, null))), showPlayOverlay && state !== PLAYING ? jsx("div", {
      css: styles.playOverlay
    }, _IconPlaySolid || (_IconPlaySolid = jsx(IconPlaySolid, {
      size: "large"
    }))) : null, jsx("video", Object.assign({
      ref: this.setVideoRef,
      src: src,
      id: this.state.videoId,
      css: styles.video,
      tabIndex: "-1",
      onCanPlay: this.hideSpinner,
      onError: onError
    }, updateMediaStateProps)), controls(this.state, actions));
    /* eslint-enable jsx-a11y/media-has-caption, jsx-a11y/no-noninteractive-tabindex */
  }
}, _VideoPlayer.propTypes = {
  // eslint-disable-next-line react/require-default-props
  makeStyles: PropTypes.func,
  // eslint-disable-next-line react/require-default-props
  styles: PropTypes.object,
  /**
   * URL of video to play
   */
  src: PropTypes.string.isRequired,
  /**
   * Function invoked on every render with state and actions.
   * Use this to provide a custom set of video controls.
   * Default player controls will be provided if undefined.
   */
  controls: PropTypes.func,
  /**
   * If set to true, the controls will never dismiss.
   */
  alwaysShowControls: PropTypes.bool,
  /**
   * If set to true shows overlay with play icon.
   */
  showPlayOverlay: PropTypes.bool,
  /**
   * Player error callback.
   */
  onError: PropTypes.func
}, _VideoPlayer.defaultProps = {
  controls: (state, actions) => {
    return jsx(VideoPlayerControls, Object.assign({}, state, {
      actions: actions
    }));
  },
  alwaysShowControls: false,
  showPlayOverlay: false,
  onError: () => {}
}, _VideoPlayer)) || _class);
export default VideoPlayer;
export { VideoPlayerControls };